import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Maximize2, Minimize2 } from 'lucide-react';
import { DarshanAlertBanner } from './DarshanAlertBanner';

interface TopBarProps {
  hideBanner?: boolean;
}

export const TopBar: React.FC<TopBarProps> = ({ hideBanner = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicDarshan = location.pathname.includes('/darshan-timings/public');
  const showBanner = !hideBanner && !isPublicDarshan;
  const [isFullscreen, setIsFullscreen] = useState<boolean>(() => !!document.fullscreenElement);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        } else if ((document.documentElement as any).webkitRequestFullscreen) {
          await (document.documentElement as any).webkitRequestFullscreen();
        } else if ((document.documentElement as any).msRequestFullscreen) {
          await (document.documentElement as any).msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (err) {
      console.warn('Error attempting to toggle fullscreen:', err);
    }
  };

  return (
    <div className="sticky top-0 z-40 w-full flex flex-col">
      {showBanner && <DarshanAlertBanner />}
      <header className="w-full h-14 border-b border-neutral-200/80 dark:border-[#262b37] bg-white dark:bg-[#14161d]/85 backdrop-blur-md px-4 flex items-center justify-between transition-colors duration-200">
        {/* Left: Brand & Status */}
        <div className="flex items-center gap-3">
          <div
            onClick={() => navigate('/')}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center shadow-md shadow-orange-500/20 group-hover:scale-105 transition-transform">
              <img
                src="/logo.png"
                alt="Dasara Logo"
                className="w-[48px] h-[48px] object-contain drop-shadow-md"
                style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}
              />
            </div>
            <div className="flex flex-col">
              <span className="font-extrabold text-sm sm:text-base tracking-tight text-neutral-900 dark:text-neutral-100">
                Integrated Command – Dasara Navaratri 2026
              </span>
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Designed by Runners Software Technologies in text-lg */}
          <div className="text-base sm:text-[13px] font-medium text-neutral-600 dark:text-neutral-300">
            Design & Developed by <strong className="font-bold text-neutral-900 dark:text-white">Runners Software Technology</strong>
          </div>

          {/* Global Full Screen Toggle */}
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit Full Screen' : 'Enter Full Screen'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 dark:border-[#262c3b] bg-neutral-100/80 dark:bg-[#181b23] text-neutral-700 dark:text-neutral-300 hover:text-orange-500 dark:hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 dark:hover:bg-orange-500/10 transition-all duration-200 cursor-pointer text-xs font-semibold shadow-xs"
          >
            {isFullscreen ? (
              <>
                <Minimize2 className="w-3.5 h-3.5 text-orange-500" />
                {!hideBanner && <span>Exit Full Screen</span>}
              </>
            ) : (
              <>
                <Maximize2 className="w-3.5 h-3.5" />
                {!hideBanner && <span>Full Screen</span>}
              </>
            )}
          </button>
        </div>
      </header>
    </div>
  );
};
